import { gql } from "graphql-request";

/**
 * Which profile row belongs to a user.
 *
 * Two answers, and both have to be accepted for now:
 *
 * - `CreatedBy` — how every existing row is owned. The platform sets it to
 *   whoever made the request, and until now a profile was always created by its
 *   own user, so it was a reliable owner.
 * - `userId` — an explicit field that already exists on the schema but which
 *   nothing has ever written, so it is null on every existing row. It is required
 *   for profiles created *on someone's behalf* (the Excel import creates them as
 *   an admin, which makes `CreatedBy` the admin and useless as an owner).
 *
 * Every profile query must go through this helper. A query written with only
 * `CreatedBy` breaks for imported users; one written with only `userId` breaks for
 * everyone who already has a profile. Keeping the rule in one place is the point.
 *
 * Exit path: once every row has `userId` populated, drop the `CreatedBy` branch
 * and this collapses to a single condition.
 */
export const profileOwnerFilter = (userId: string) => ({
  or: [{ userId: { eq: userId } }, { CreatedBy: { eq: userId } }],
});

// NOTE: there is deliberately no "active profile" variant pairing ownership with
// `IsDeleted: { eq: false }`.
//
// Nothing ever writes `IsDeleted` on a userprofile — the insert does not set it —
// so it is null on every row, and `eq: false` does not match null. The condition
// therefore excluded every profile rather than excluding deleted ones: it made
// profile updates silently match zero rows and report success. Deletes here are
// hard (`isHardDelete: true`), so there are no soft-deleted profiles to exclude in
// the first place.

export const GET_USER_PROFILES = gql`
  query GetUserProfiles(
    $where: userprofileFilterInput
    $order: [DynamicSortInput!]
    $pageNo: Int!
    $pageSize: Int!
  ) {
    getuserprofiles(
      where: $where
      order: $order
      paging: { pageNo: $pageNo, pageSize: $pageSize }
    ) {
      totalCount
      totalPages
      hasNextPage
      hasPreviousPage
      items {
        ItemId
        CreatedDate
        CreatedBy
        LastUpdatedDate
        LastUpdatedBy
        Language
        OrganizationId
        Tags
        DeletedDate
        gender
        dob
        designation
        department
        company
        website
        address
        country
        description
        backgroundImageUrl
        wikiSaved
        newsSaved
        newsRead
        wikiDraftIds
        newsDraftIds
        savedPostIds
        preferredOrganizationId
      }
    }
  }
`;

export const GET_USER_PROFILE_COUNTRIES = gql`
  query GetUserProfileCountries(
    $where: userprofileFilterInput
    $order: [DynamicSortInput!]
    $pageNo: Int!
    $pageSize: Int!
  ) {
    getuserprofiles(
      where: $where
      order: $order
      paging: { pageNo: $pageNo, pageSize: $pageSize }
    ) {
      totalCount
      items {
        CreatedBy
        userId
        country
      }
    }
  }
`;

export const GET_USER_PREFERENCES_QUERY = gql`
  query GetUserPreferences(
    $where: userprofileFilterInput
    $order: [DynamicSortInput!]
  ) {
    getuserprofiles(where: $where, order: $order) {
      items {
        ItemId
        CreatedBy
        preferences {
          language
          lightMode
          notificationsEnabled
        }
      }
    }
  }
`;

export const CREATE_USER_PREFERENCES_MUTATION = gql`
  mutation InsertUserProfile(
    $filter: String!
    $language: String
    $lightMode: Boolean
    $notificationsEnabled: Boolean
  ) {
    insertuserprofile(
      input: {
        preferences: {
          language: $language
          lightMode: $lightMode
          notificationsEnabled: $notificationsEnabled
        }
      }
    ) {
      acknowledged
      totalImpactedData
      itemId
      message
    }
  }
`;

export const UPDATE_USER_PREFERENCES_MUTATION = gql`
  mutation UpdateUserProfile(
    $where: userprofileFilterInput
    $language: String
    $lightMode: Boolean
    $notificationsEnabled: Boolean
  ) {
    updateuserprofile(
      where: $where
      input: {
        preferences: {
          language: $language
          lightMode: $lightMode
          notificationsEnabled: $notificationsEnabled
        }
      }
    ) {
      acknowledged
      totalImpactedData
      itemId
      message
    }
  }
`;

// NOTE: preferredOrganizationId is intentionally absent from INSERT_USER_PROFILE.
// The field does not yet exist in the Data Gateway schema (open item 5 in
// V4-MIGRATION-STATUS.md). Including it would fail GraphQL schema validation and
// prevent the whole profile from saving. Use UPDATE_PREFERRED_ORG_MUTATION instead,
// called separately after this mutation succeeds.
// Unblock: madp run blocks-data-gateway (requires portal auth — see open item 5).
export const INSERT_USER_PROFILE = gql`
  mutation InsertUserProfile(
    $userId: String
    $gender: String
    $dob: String
    $designation: String
    $department: String
    $company: String
    $website: String
    $address: String
    $country: String
    $description: String
    $backgroundImageUrl: String
    $newsSaved: [String]
    $newsRead: [String]
    $newsDraftIds: [String]
  ) {
    insertuserprofile(
      input: {
        userId: $userId
        gender: $gender
        dob: $dob
        designation: $designation
        department: $department
        company: $company
        website: $website
        address: $address
        country: $country
        description: $description
        backgroundImageUrl: $backgroundImageUrl
        newsSaved: $newsSaved
        newsRead: $newsRead
        newsDraftIds: $newsDraftIds
      }
    ) {
      acknowledged
      totalImpactedData
      itemId
      message
    }
  }
`;

// NOTE: preferredOrganizationId is intentionally absent. See INSERT_USER_PROFILE note.
// Use UPDATE_PREFERRED_ORG_MUTATION for preference changes.
export const UPDATE_USER_PROFILE = gql`
  mutation UpdateUserProfile(
    $where: userprofileFilterInput
    $gender: String
    $dob: String
    $designation: String
    $department: String
    $company: String
    $website: String
    $address: String
    $country: String
    $description: String
    $backgroundImageUrl: String
    $archivedEventIds: [String!]
    $wikiSaved: [String]
    $newsSaved: [String]
    $newsRead: [String]
    $wikiDraftIds: [String]
    $newsDraftIds: [String]
  ) {
    updateuserprofile(
      where: $where
      input: {
        gender: $gender
        dob: $dob
        designation: $designation
        department: $department
        company: $company
        website: $website
        address: $address
        country: $country
        description: $description
        backgroundImageUrl: $backgroundImageUrl
        archivedEventIds: $archivedEventIds
        wikiSaved: $wikiSaved
        newsSaved: $newsSaved
        newsRead: $newsRead
        wikiDraftIds: $wikiDraftIds
        newsDraftIds: $newsDraftIds
      }
    ) {
      acknowledged
      totalImpactedData
      itemId
      message
    }
  }
`;

// NOTE: no call sites — the live purge path is DELETE_MANY_USER_PROFILE_BY_CREATOR.
export const DELETE_USER_PROFILE = gql`
  mutation DeleteUserProfile($where: userprofileFilterInput) {
    deleteuserprofile(
      where: $where
      input: { isHardDelete: true }
    ) {
      acknowledged
      totalImpactedData
      itemId
      message
    }
  }
`;

// Bulk delete: every profile created by a user (used by the user-deletion purge).
export const DELETE_MANY_USER_PROFILE_BY_CREATOR = gql`
  mutation DeleteManyUserProfileByCreator($where: userprofileFilterInput) {
    deleteManyuserprofile(
      where: $where
      input: { isHardDelete: true }
    ) {
      acknowledged
      totalImpactedData
      itemId
      message
    }
  }
`;

// Post saves
export const UPDATE_USER_SAVED_POSTS_MUTATION = gql`
  mutation UpdateUserSavedPosts($itemId: String!, $savedPostIds: [String]!) {
    updateuserprofile(
      where: { ItemId: { eq: $itemId } }
      input: { savedPostIds: $savedPostIds }
    ) {
      acknowledged
      totalImpactedData
      itemId
      message
    }
  }
`;

export const UPDATE_POST_SAVE_COUNT_MUTATION = gql`
  mutation UpdatePostSaveCount($itemId: String!, $saveCount: Int!) {
    updatepost(
      where: { ItemId: { eq: $itemId } }
      input: { saveCount: $saveCount }
    ) {
      acknowledged
      totalImpactedData
      itemId
      message
    }
  }
`;

// NOTE: GET_PREFERRED_ORG_QUERY selects preferredOrganizationId, which does not yet
// exist in the Data Gateway schema (open item 5 in V4-MIGRATION-STATUS.md). GraphQL
// validates the query against the schema before executing, so this query currently
// fails validation — it does NOT return null, it throws. useGetPreferredOrgQuery sets
// retry:0 so login is not delayed by failed retries.
// Unblock: madp run blocks-data-gateway (requires portal auth — see open item 5).
export const GET_PREFERRED_ORG_QUERY = gql`
  query GetPreferredOrg($where: userprofileFilterInput) {
    getuserprofiles(where: $where) {
      items {
        ItemId
        CreatedBy
        preferredOrganizationId
      }
    }
  }
`;

export const CLEAR_PREFERRED_ORG_MUTATION = gql`
  mutation ClearPreferredOrg($where: userprofileFilterInput) {
    updateuserprofile(
      where: $where
      input: { preferredOrganizationId: null }
    ) {
      acknowledged
      totalImpactedData
      itemId
      message
    }
  }
`;

// NOTE: UPDATE_PREFERRED_ORG_MUTATION also requires the preferredOrganizationId schema
// field to exist in the Data Gateway schema (same open item 5). When the field is
// absent this mutation fails validation — which is the expected behaviour. The caller
// (edit-profile.tsx) catches this error and shows a partial-success message instead
// of a blanket success. No code change is needed once the schema is synced.
// Unblock: madp run blocks-data-gateway (requires portal auth — see open item 5).
export const UPDATE_PREFERRED_ORG_MUTATION = gql`
  mutation UpdatePreferredOrg($where: userprofileFilterInput, $preferredOrganizationId: String) {
    updateuserprofile(
      where: $where
      input: { preferredOrganizationId: $preferredOrganizationId }
    ) {
      acknowledged
      totalImpactedData
      itemId
      message
    }
  }
`;

export const GET_ALL_USER_PROFILES = gql`
  query GetAllUserProfiles($month: String, $orgId: String) {
    getuserprofiles(
      where: {
        dob: { contains: $month }
        OrganizationId: { eq: $orgId }
      }
      order: []
      paging: { pageNo: 1, pageSize: 100 }
    ) {
      items {
        ItemId
        CreatedBy
        dob
      }
      totalCount
    }
  }
`;
